import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { NotificationService } from '../../../services/notification.service';
import { AuthService } from '../../../services/auth.service';
import { ResetPasswordRequest } from '../../../models/auth.models';
import { Log, IShortLogger } from '../../../services/logger.service';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.css'
})
export class ResetPasswordComponent implements OnInit {
  private readonly logger: IShortLogger<ResetPasswordComponent> = Log.create(ResetPasswordComponent);

  resetPasswordForm: FormGroup;
  isLoading: boolean = false;
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;
  emailFromQuery: string = '';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private notification: NotificationService
  ) {
    this.resetPasswordForm = this.formBuilder.group({
      loginId: ['', [Validators.required, Validators.email]],
      otpCode: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
      newPassword: ['', [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(100),
        this.passwordValidator
      ]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    // Get email from query parameters if available
    this.route.queryParams.subscribe(params => {
      if (params['email']) {
        this.emailFromQuery = params['email'];
        this.resetPasswordForm.patchValue({ loginId: this.emailFromQuery });
      }
    });
  }

  get loginId() { return this.resetPasswordForm.get('loginId'); }
  get otpCode() { return this.resetPasswordForm.get('otpCode'); }
  get newPassword() { return this.resetPasswordForm.get('newPassword'); }
  get confirmPassword() { return this.resetPasswordForm.get('confirmPassword'); }

  passwordValidator(control: AbstractControl): { [key: string]: any } | null {
    const value = control.value;
    if (!value) return null;

    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumeric = /[0-9]/.test(value);
    const hasSpecialChar = /[@$!%*?&]/.test(value);

    const valid = hasUpperCase && hasLowerCase && hasNumeric && hasSpecialChar;

    if (!valid) {
      return {
        passwordStrength: {
          hasUpperCase,
          hasLowerCase,
          hasNumeric,
          hasSpecialChar
        }
      };
    }

    return null;
  }

  passwordMatchValidator(group: AbstractControl): { [key: string]: any } | null {
    const password = group.get('newPassword');
    const confirmPassword = group.get('confirmPassword');

    if (!password || !confirmPassword) return null;

    return password.value === confirmPassword.value ? null : { passwordMismatch: true };
  }

  togglePasswordVisibility(field: 'password' | 'confirm'): void {
    if (field === 'password') {
      this.showPassword = !this.showPassword;
    } else {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }

  submitResetPassword(): void {
    if (this.resetPasswordForm.invalid) {
      this.markFormGroupTouched();
      return;
    }

    this.isLoading = true;
    const formValue = this.resetPasswordForm.value;
    const request: ResetPasswordRequest = {
      LoginId: formValue.loginId,
      OtpCode: formValue.otpCode,
      NewPassword: formValue.newPassword,
      ConfirmPassword: formValue.confirmPassword
    };

    this.authService.resetPassword(request).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.notification.notify('Password has been reset successfully. You can now login with your new password.', 'success', 5000);

        // Navigate to login page
        this.router.navigate(['/login']);
      },
      error: (error) => {
        this.isLoading = false;
        this.logger.e('Reset password error:', error);

        let errorMessage = 'An error occurred while resetting your password. Please try again.';

        // Try to extract the exact error message from various possible formats
        if (error.status === 404) {
          // Handle 404 specifically - the error message is likely in error.error
          errorMessage = error.error || 'User not found';
        } else if (error.error) {
          if (typeof error.error === 'string') {
            errorMessage = error.error;
          } else if (error.error.message) {
            errorMessage = error.error.message;
          } else if (error.error.title) {
            errorMessage = error.error.title;
          }
        } else if (error.message) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }

        this.notification.notify(errorMessage, 'error', 5000);
      }
    });
  }

  private markFormGroupTouched(): void {
    Object.keys(this.resetPasswordForm.controls).forEach(key => {
      const control = this.resetPasswordForm.get(key);
      control?.markAsTouched();
    });
  }

  goBackToForgotPassword(): void {
    this.router.navigate(['/forgot-password']);
  }

  goBackToLogin(): void {
    this.router.navigate(['/login']);
  }

  resendOtp(): void {
    if (!this.loginId?.value) {
      this.notification.notify('Please enter your email address first', 'warning', 3000);
      return;
    }

    this.authService.forgotPassword({ LoginId: this.loginId.value }).subscribe({
      next: (response) => {
        this.notification.notify('A new OTP has been sent to your email address.', 'success', 3000);
      },
      error: (error) => {
        this.logger.e('Resend OTP error:', error);

        let errorMessage = 'Failed to resend OTP. Please try again.';

        // Try to extract the exact error message from various possible formats
        if (error.status === 404) {
          // Handle 404 specifically - the error message is likely in error.error
          errorMessage = error.error || 'User with this email address does not exist';
        } else if (error.error) {
          if (typeof error.error === 'string') {
            errorMessage = error.error;
          } else if (error.error.message) {
            errorMessage = error.error.message;
          } else if (error.error.title) {
            errorMessage = error.error.title;
          }
        } else if (error.message) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }

        this.notification.notify(errorMessage, 'error', 3000);
      }
    });
  }
}
